"use client";

// Anonymous/public RFQ endpoints — same-origin proxy pattern as customer-auth-api.ts
// (frontend/src/app/api/[...path]/route.ts forwards to the backend and injects the
// resolved x-domain-host automatically, so no domain plumbing is needed here).

import type { ResolvedQuoteFormDto } from "./quote-form-evaluator";
import type { ResolvedDefaultImages } from "@/components/common/SafeImage";

export type PublicProductQuoteType = "Block" | "Slab" | "CustomSize";

/** Mirrors the subset of the public product-detail response
 * (PublicController.GetProduct) this feature needs — the full DTO has many more fields
 * used elsewhere on the product page, this is intentionally narrow. */
export type PublicProductSummary = {
  id: string;
  slug: string;
  name: string;
  sku?: string;
  imageUrl?: string;
  categoryName?: string;
  productQuoteType?: PublicProductQuoteType | null;
};

export async function getPublicProductBySlug(slug: string): Promise<PublicProductSummary | null> {
  try {
    const res = await fetch(`/api/public/products/${encodeURIComponent(slug)}`);
    if (!res.ok) return null;
    return (await res.json()) as PublicProductSummary;
  } catch {
    return null;
  }
}

/** Default Image / Showcase Image audit fix — the RFQ product summary card previously never
 * fetched this at all, so a quoted product with no image of its own skipped straight past the
 * admin-configured Product Default Image to SafeImage's static bundled asset, unlike every
 * other product card on the storefront (home, shop, gallery, related products, category
 * detail), which all thread `defaults` through from api-client.ts's getDefaultImages(). That
 * helper resolves to an internal-only backend URL when called client-side (see its baseUrl
 * fallback chain), so this reuses the same same-origin proxy pattern as this file's sibling
 * functions instead of importing it directly. Empty on any failure — SafeImage's own static
 * asset fallback still guarantees a valid image either way. */
export async function getPublicDefaultImages(): Promise<ResolvedDefaultImages> {
  try {
    const res = await fetch("/api/public/default-images");
    if (!res.ok) return {};
    return (await res.json()) as ResolvedDefaultImages;
  } catch {
    return {};
  }
}

/** Reuses the existing public domain-config endpoint (ResolvedDomainConfiguration now
 * carries AllowGuestQuoteRequests) rather than a new settings endpoint — this is the same
 * domain-scoped value the admin "Teklif Ayarları" page manages. Defaults to `false` (the
 * same safe default the backend itself uses) on any failure, since an unknown guest-setting
 * must never be treated as "guest RFQ allowed". */
export async function getAllowGuestQuoteRequests(): Promise<boolean> {
  try {
    const res = await fetch("/api/public/config");
    if (!res.ok) return false;
    const data = (await res.json()) as { allowGuestQuoteRequests?: boolean };
    return data.allowGuestQuoteRequests === true;
  } catch {
    return false;
  }
}

/** Faz 8 — a language option for a customer-facing picker: the bare code plus its resolved
 * display names, mirrors backend PublicLanguageOptionDto. */
export type PublicLanguageOptionDto = { code: string; name: string; nativeName: string };

/** Public Multilingual Content Completion + RFQ Communication Language phase — the RFQ
 * "İletişim Dili" picker's language list, sourced from the dedicated
 * /api/public/communication-languages endpoint (the global /admin/languages catalog),
 * deliberately NOT from /api/public/config's supportedLanguageOptions (this domain's public/
 * storefront active languages — a different, independent language scope; see
 * PublicController.GetCommunicationLanguages's doc comment). Empty on any failure — callers
 * must treat that as "options not loaded yet", never silently skip the required-selection UI. */
export async function getCommunicationLanguageOptions(): Promise<PublicLanguageOptionDto[]> {
  try {
    const res = await fetch("/api/public/communication-languages");
    if (!res.ok) return [];
    const data = (await res.json()) as PublicLanguageOptionDto[];
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
}

export type ResolvedQuoteFormOutcome =
  | { kind: "found"; form: ResolvedQuoteFormDto }
  | { kind: "notConfigured" }
  | { kind: "error"; message: string };

export async function getResolvedQuoteForm(productId: string): Promise<ResolvedQuoteFormOutcome> {
  try {
    const res = await fetch(`/api/public/products/${encodeURIComponent(productId)}/quote-form`);
    if (res.status === 404) return { kind: "notConfigured" };
    if (!res.ok) {
      const body = await res.json().catch(() => null);
      return { kind: "error", message: body?.message || "Form yüklenemedi." };
    }
    const form = (await res.json()) as ResolvedQuoteFormDto;
    return { kind: "found", form };
  } catch {
    return { kind: "error", message: "Form yüklenemedi." };
  }
}

export type QuoteRequestAnswerInput = { fieldId: string; value: unknown };

export type SubmitGuestQuoteRequestCommand = {
  productId: string;
  quoteFormTemplateVersionId: string;
  idempotencyKey?: string;
  guestFirstName: string;
  guestLastName: string;
  guestEmail: string;
  guestPhone: string;
  answers: QuoteRequestAnswerInput[];
  /** Language for the Faz 4 quote-access verification email — defaults to "en" server-side
   * if omitted, but the storefront always has the active language available. */
  languageCode?: string;
  /** Faz 8 — mandatory; the language the guest chose to communicate in, snapshotted
   * immutably onto the RFQ. Deliberately a separate field from languageCode above: that one
   * is just the verification-email locale, this is the RFQ's permanent business-data
   * snapshot (the caller sends the same selected value for both, but the backend never
   * conflates the two). */
  communicationLanguageCode: string;
};

export type QuoteRequestSubmitResultDto = { id: string; requestNumber: string };

/** Raw response envelope — mirrors the non-throwing {ok,status,data} convention already
 * used throughout customer-auth-api.ts. Interpreting status codes into UI-facing outcomes
 * (validation errors / stale form / guest-disabled / not-found) is centralized in
 * interpretQuoteSubmitResponse below so both the guest and authenticated submit paths
 * (the latter living in customer-auth-api.ts) share identical handling. */
export type RawApiResult<T> = { ok: boolean; status: number; data: T | null };

async function postJson<T>(path: string, body: unknown): Promise<RawApiResult<T>> {
  const response = await fetch(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

  let data: T | null = null;
  try {
    data = (await response.json()) as T;
  } catch {
    data = null;
  }

  return { ok: response.ok, status: response.status, data };
}

export async function submitGuestQuoteRequest(cmd: SubmitGuestQuoteRequestCommand): Promise<RawApiResult<QuoteRequestSubmitResultDto>> {
  return postJson<QuoteRequestSubmitResultDto>("/api/public/quote-requests", cmd);
}

export type QuoteSubmitOutcome =
  | { kind: "success"; result: QuoteRequestSubmitResultDto }
  | { kind: "validationError"; errors: Record<string, string[]> }
  | { kind: "stale"; message: string }
  | { kind: "guestDisabled"; message: string }
  | { kind: "notFound"; message: string }
  | { kind: "unknownError"; message: string };

/** Shared interpreter for the guest and authenticated submit responses (both endpoints
 * use identical error shapes per backend QuoteRequestContracts.cs — see
 * PublicController.SubmitGuestQuoteRequest / CustomerQuoteRequestsController.Submit). */
export function interpretQuoteSubmitResponse(result: RawApiResult<unknown>): QuoteSubmitOutcome {
  const body = result.data as { message?: string; errors?: Record<string, string[]>; id?: unknown; requestNumber?: unknown } | null;

  if (result.ok) {
    // A 2xx alone is never sufficient proof an RFQ was actually created — callers (e.g.
    // RequestQuoteView) auto-redirect the user to their quote-request list on "success",
    // so this must only fire when the response genuinely carries a usable RFQ reference.
    if (body && typeof body.id === "string" && body.id.length > 0 && typeof body.requestNumber === "string" && body.requestNumber.length > 0) {
      return { kind: "success", result: { id: body.id, requestNumber: body.requestNumber } };
    }
    return { kind: "unknownError", message: "Teklif talebi durumu doğrulanamadı. Lütfen tekrar deneyin." };
  }
  if (result.status === 400 && body?.errors) {
    return { kind: "validationError", errors: body.errors };
  }
  if (result.status === 409) {
    return { kind: "stale", message: body?.message || "Teklif formu güncellendi. Lütfen formu yenileyip tekrar gönderin." };
  }
  if (result.status === 403) {
    return { kind: "guestDisabled", message: body?.message || "Bu domain için misafir teklif talebi kapalıdır." };
  }
  if (result.status === 404) {
    return { kind: "notFound", message: body?.message || "Ürün veya form bulunamadı." };
  }
  return { kind: "unknownError", message: body?.message || "Bir hata oluştu. Lütfen tekrar deneyin." };
}
